import moment from 'moment';
import { millisecondsToSeconds, minutesToHours, secondsToMinutes } from 'date-fns';

type CommonUserType = string | null | undefined;
type CommonDateType = number | null | undefined;

/**
 * All utility functions require format parameter
 * Components should use hooks from 'Hooks/useDateFormats' to get formats and pass them here
 */

export const getFormattedDate = (date: string, pattern: string) => {
    return moment(new Date(date)).format(pattern);
};

export const getFormattedDateTimeAndUser = (
    createdAt: CommonDateType,
    createdBy: CommonUserType,
    modifiedAt: CommonDateType,
    modifiedBy: CommonUserType,
    pattern: string
) => {
    let formattedDateTime = '';
    formattedDateTime = modifiedAt ? moment(new Date(modifiedAt)).format(pattern) : '';
    formattedDateTime = createdAt ? moment(new Date(createdAt)).format(pattern) : formattedDateTime;
    const user = modifiedBy || createdBy;

    return `${formattedDateTime} by ${user}`;
};

export const getUserAndFormattedDateTime = (
    createdAt: CommonDateType,
    createdBy: CommonUserType,
    modifiedAt: CommonDateType,
    modifiedBy: CommonUserType,
    pattern: string
) => {
    let formattedDateTime = '';
    formattedDateTime = modifiedAt ? moment(new Date(modifiedAt)).format(pattern) : '';
    formattedDateTime = createdAt ? moment(new Date(createdAt)).format(pattern) : formattedDateTime;
    const user = modifiedBy || createdBy;

    return `${user} sent changes for approval – ${formattedDateTime}`;
};

export const { timeZone } = Intl.DateTimeFormat().resolvedOptions();

export const getUTCTimeZoneOffset = () => {
    const today = new Date();
    const sign = today.getTimezoneOffset() > 0 ? '-' : '+';
    const offset = Math.abs(today.getTimezoneOffset());
    const hours = Math.floor(offset / 60)
        .toString()
        .padStart(2, '0');
    const minutes = (offset % 60).toString().padStart(2, '0');
    return `${sign + hours}:${minutes}`;
};

export const getDateFromArray = (dateArray: [number, number, number]): Date | null => {
    if (!dateArray) {
        return null;
    }
    if (dateArray instanceof Date) {
        return dateArray;
    }
    const [year, month, day] = dateArray;
    return new Date(year, month - 1, day);
};

export const getObjectFromDate = (date: Date | null): { year: number; month: number; day: number } | null => {
    if (!date) {
        return null;
    }

    return {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate()
    };
};

export const getElapsedTime = (start: Date, end: Date) => {
    const elapsedMilliseconds = start.getTime() - end.getTime();

    const elapsedSeconds = millisecondsToSeconds(elapsedMilliseconds);
    const elapsedMinutes = secondsToMinutes(elapsedSeconds);
    const elapsedHours = minutesToHours(elapsedMinutes);
    const elapsedDays = Math.ceil(elapsedHours / 24);

    if (elapsedDays > 1) {
        return { elapsedTime: elapsedDays, format: 'd' };
    }
    if (elapsedHours > 0) {
        return { elapsedTime: elapsedHours, format: 'h' };
    }
    if (elapsedMinutes > 0) {
        return { elapsedTime: elapsedMinutes, format: 'm' };
    }
    return { elapsedTime: elapsedSeconds, format: 's' };
};

export const formatDateInString = (inputString: string, pattern: string) => {
    const regex = /\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(\.\d{6})?/;
    const dateMatch = inputString.match(regex);

    if (dateMatch) {
        const formattedDate = moment(dateMatch[0].split('.')[0]).format(pattern);
        return inputString.replace(dateMatch[0], formattedDate);
    }

    return inputString;
};

export const formatEpochToLocalDateTime = (date: number, pattern: string) => {
    return date ? moment(new Date(date)).format(pattern) : '';
};
